import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useExam } from '../hooks/useExams';
import { useExamStore } from '../stores/examStore';
import { examApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { 
  Clock, 
  ArrowLeft, 
  ArrowRight, 
  Flag,
  Bookmark,
  BookmarkCheck,
  AlertTriangle,
  CheckCircle,
  Send
} from 'lucide-react';
import { 
  FadeIn, 
  ScaleIn, 
  TimerDisplay,
  ConfettiEffect,
  LoadingSpinner 
} from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

export function ExamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const examId = parseInt(id || '0');
  
  const { data: exam, isLoading } = useExam(examId);
  const { 
    currentAttempt, 
    answers, 
    timeRemaining, 
    isTimerRunning,
    flaggedQuestions,
    setCurrentExam,
    setCurrentQuestions,
    setCurrentAttempt,
    setAnswer,
    setTimeRemaining,
    setTimerRunning,
    toggleFlagQuestion,
    clearExam
  } = useExamStore();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showTimeUp, setShowTimeUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Initialize exam
  useEffect(() => {
    if (exam) {
      setCurrentExam(exam);
      setCurrentQuestions(exam.questions || []);
      setTimeRemaining((exam.durationMinutes || 30) * 60);
    }
    return () => clearExam();
  }, [exam]);

  // Timer
  useEffect(() => {
    if (!isTimerRunning || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(timeRemaining - 1);
      if (timeRemaining <= 1) {
        setShowTimeUp(true);
        handleSubmit(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerRunning, timeRemaining]);

  // Start exam
  const handleStart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to start the exam');
      navigate('/login');
      return;
    }

    try {
      const res = await examApi.startExam(examId);
      setCurrentAttempt(res.data);
      setTimerRunning(true);
      toast.success('Exam started! Good luck!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start exam');
    }
  };

  // Submit exam
  const handleSubmit = async (autoSubmit = false) => {
    if (!currentAttempt) return;
    
    setIsSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([questionId, selectedOption]) => ({
        questionId: parseInt(questionId),
        selectedOption,
      }));

      const res = await examApi.submitExam(examId, answersArray);
      setTimerRunning(false);
      setShowConfetti(true);
      
      if (!autoSubmit) {
        toast.success(`Exam submitted! Score: ${res.data.percentage}%`);
      }
      
      navigate(`/exams/${examId}/result`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit exam');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigate questions
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < (exam?.questions?.length || 0)) {
      setCurrentQuestionIndex(index);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!exam) {
    return (
      <FadeIn>
        <div className="text-center py-16">
          <AlertTriangle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Exam not found</h2>
          <p className="text-slate-500">The exam you're looking for doesn't exist</p>
        </div>
      </FadeIn>
    );
  }

  const questions = exam.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const answeredCount = Object.keys(answers).length;

  // Exam not started - show info
  if (!currentAttempt) {
    return (
      <div className="max-w-2xl mx-auto">
        <FadeIn>
          <button
            onClick={() => navigate('/exams')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Exams
          </button>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-primary-600 to-primary-500 p-8 text-white">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
                exam.type === 'live' ? 'bg-red-500/30' : 'bg-secondary-500/30'
              }`}>
                {exam.type === 'live' ? '🔴 Live Exam' : '💚 Practice Exam'}
              </span>
              <h1 className="text-3xl font-bold mb-2">{exam.titleBn || exam.title}</h1>
              <p className="text-primary-100">{exam.subject?.nameBn || exam.subject?.name}</p>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Clock className="w-6 h-6 text-primary-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-slate-900">{exam.durationMinutes}</div>
                  <div className="text-xs text-slate-500">Minutes</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-secondary-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-slate-900">{questions.length}</div>
                  <div className="text-xs text-slate-500">Questions</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="w-6 h-6 text-accent-600 mx-auto mb-2 font-bold text-xl">Σ</div>
                  <div className="text-lg font-bold text-slate-900">{exam.totalMarks}</div>
                  <div className="text-xs text-slate-500">Total Marks</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="w-6 h-6 text-purple-600 mx-auto mb-2 font-bold text-xl">✓</div>
                  <div className="text-lg font-bold text-slate-900">{exam.passMarks}</div>
                  <div className="text-xs text-slate-500">Pass Marks</div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Instructions:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    The exam has {questions.length} multiple choice questions
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    Total time: {exam.durationMinutes} minutes
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    Each question has 4 options, only one is correct
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    You can flag questions to review later
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    Exam will auto-submit when time runs out
                  </li>
                </ul>
              </div>

              <motion.button
                onClick={handleStart}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-bold rounded-xl hover:shadow-lg transition-shadow"
              >
                Start Exam
              </motion.button>
            </div>
          </div>
        </FadeIn>
      </div>
    );
  }

  // Exam in progress
  return (
    <div className="max-w-4xl mx-auto">
      <ConfettiEffect trigger={showConfetti} />
      
      {/* Header */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur-lg border-b border-slate-200 -mx-4 px-4 py-3 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 truncate max-w-[200px] sm:max-w-md">
              {exam.titleBn || exam.title}
            </h2>
            <p className="text-sm text-slate-500">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
          </div>
          <TimerDisplay seconds={timeRemaining} />
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="w-full bg-slate-200 rounded-full h-2">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
              animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        {currentQuestion && (
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8"
          >
            {/* Question Text */}
            <div className="flex items-start justify-between gap-4 mb-8">
              <div className="flex-1">
                <span className="text-sm text-slate-400 font-medium mb-2 block">
                  Question {currentQuestionIndex + 1}
                </span>
                <h3 className="text-lg md:text-xl font-semibold text-slate-900 leading-relaxed">
                  {currentQuestion.questionTextBn || currentQuestion.questionText}
                </h3>
              </div>
              <button
                onClick={() => toggleFlagQuestion(currentQuestion.id)}
                className={`p-2 rounded-xl transition-colors ${
                  flaggedQuestions.includes(currentQuestion.id)
                    ? 'bg-accent-100 text-accent-600'
                    : 'bg-slate-100 text-slate-400 hover:text-accent-600'
                }`}
              >
                {flaggedQuestions.includes(currentQuestion.id) ? (
                  <BookmarkCheck className="w-5 h-5" />
                ) : (
                  <Flag className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {[
                { key: 'a', text: currentQuestion.optionA },
                { key: 'b', text: currentQuestion.optionB },
                { key: 'c', text: currentQuestion.optionC },
                { key: 'd', text: currentQuestion.optionD },
              ].map((option) => (
                <motion.button
                  key={option.key}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setAnswer(currentQuestion.id, option.key)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    answers[currentQuestion.id] === option.key
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 hover:border-primary-300 hover:bg-slate-50'
                  }`}
                >
                  <span className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    answers[currentQuestion.id] === option.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {option.key.toUpperCase()}
                  </span>
                  <span className="text-slate-700 font-medium">{option.text}</span>
                  {answers[currentQuestion.id] === option.key && (
                    <CheckCircle className="w-5 h-5 text-primary-500 ml-auto flex-shrink-0" />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => goToQuestion(currentQuestionIndex - 1)}
          disabled={currentQuestionIndex === 0}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </motion.button>

        {/* Question Navigator */}
        <div className="hidden md:flex items-center gap-2">
          {questions.map((_question: any, index: number) => (
            <button
              key={index}
              onClick={() => goToQuestion(index)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                index === currentQuestionIndex
                  ? 'bg-primary-600 text-white'
                  : answers[questions[index].id]
                    ? 'bg-secondary-100 text-secondary-700'
                    : flaggedQuestions.includes(questions[index].id)
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {isLastQuestion ? (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSubmitConfirm(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
          >
            <Send className="w-4 h-4" />
            Submit
          </motion.button>
        ) : (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => goToQuestion(currentQuestionIndex + 1)}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
          >
            Next
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        )}
      </div>

      {/* Mobile Question Navigator */}
      <div className="md:hidden mt-4 flex flex-wrap gap-2 justify-center">
        {questions.map((_question: any, index: number) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
              index === currentQuestionIndex
                ? 'bg-primary-600 text-white'
                : answers[questions[index].id]
                  ? 'bg-secondary-100 text-secondary-700'
                  : flaggedQuestions.includes(questions[index].id)
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-slate-100 text-slate-500'
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {/* Submit Confirmation Modal */}
      <AnimatePresence>
        {showSubmitConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-accent-100 text-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Submit Exam?</h3>
                <p className="text-slate-500 mb-6">
                  You have answered {answeredCount} out of {questions.length} questions.
                  {answeredCount < questions.length && (
                    <span className="text-accent-600 block mt-1">
                      {questions.length - answeredCount} questions are unanswered!
                    </span>
                  )}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSubmitConfirm(false)}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                    className="flex-1 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-semibold rounded-xl hover:shadow-lg transition-shadow disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Now'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Time Up Modal */}
      <AnimatePresence>
        {showTimeUp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full text-center"
            >
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Time's Up!</h3>
              <p className="text-slate-500 mb-4">
                Your exam is being submitted automatically...
              </p>
              <LoadingSpinner />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
